import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Autocomplete,
  Tabs,
  Tab,
} from '@mui/material';
import { withTransaction } from '@elastic/apm-rum-react';
import { useFormik } from 'formik';
import { string, object, array } from 'yup';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import useTabChange from '../../components/Common/styled/Tabs';
import { ReactComponent as DropdownArrow } from '../../assets/DropdownArrow.svg';
import { ReactComponent as SearchIconNew } from '../../assets/SearchIconNew.svg';
import GroupDialog from '../../components/GroupDialog';
import GroupPermission from '../../components/GroupPermission';
import useOrganization from '../../hooks/UseOrganization';
import { ReactComponent as NoDataImage } from '../../assets/NoDataImage.svg';
import { ReactComponent as NoDataCreated } from '../../assets/NoDataCreated.svg';
import DeleteDialog from '../../components/Common/DeleteDialog';
import useUserGroupPermission from '../../hooks/useUserGroupPermission';
import MultiSelect from '../../components/Common/MultiSelect';

/**
 * @component GroupSetting
 * @description Renders the group management interface, including group list, group creation, and member management.
 * @param {Object} props - Component props
 * @param {Object} props.state - The current state of the groups tab
 * @param {Function} props.updateGroupPermission - Function to update group permissions.
 * @returns {JSX.Element}
 */

const GroupSetting = () => {
  const orgName = useSelector((states) => states.common.orgName);
  const {
    getGroupList,
    createGroup,
    groupDelete,
    getApplicationList,
    getMemberList,
    getDefaultPermission,
    updateGroup,
  } = useOrganization();
  const { fetchUserDetails, updateGroupPermission } = useUserGroupPermission();
  const [tabIndex, handleTabChange, setTabIndex] = useTabChange(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [triggerGrouplist, setTriggerGrouplist] = useState(true);
  const [userData, setUserData] = useState([]);
  const [open, setOpen] = useState(false);
  const [groupList, setGroupList] = useState([]);
  const [productList, setProductList] = useState([]);
  const theme = useTheme();
  const handleOpen = useCallback(() => setOpen(true), [setOpen]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [userList, setUserList] = useState(null);

  const filteredGroupList = groupList?.filter((item) =>
    item?.name?.toLowerCase()?.includes(searchText?.toLowerCase() || '')
  );

  const handleSearchChange = useCallback((event) => {
    setSearchText(event?.target?.value?.toLowerCase()); // Lowercase for case-insensitive search
  }, []);

  /**
   * @type {Object}
   * @description formikForm - Formik configuration for group form.
   * @property {Object} initialValues - The initial values of the form
   * @property {string} initialValues.name - The initial name of the group
   * @property {string} [initialValues.description] - The initial description of the group
   * @property {Object} validationSchema - The validation schema for the form
   * @property {Function} onSubmit - The function to call when the form is submitted
   * @property {Function} onSubmit.formData - The form data
   */

  const formikForm = useFormik({
    initialValues: {
      name: '',
      users: [],
      description: '',
    },
    validationSchema: object({
      name: string().required('Required'),
      users: array()
        .nullable()
        .min(0, 'Please select at least one user')
        .of(
          object().shape({
            label: string().required('Required'),
            value: string().required('Required'),
          })
        )
        .optional('Please select at least one user'),
      description: string()
        .optional()
        .min(2, 'Description must be at least 2 characters')
        .max(320, 'Description must be at most 320 characters'),
    }),
    onSubmit: (formData) => {
      const values = {
        ...formData,
        users: formData?.users?.map((user) => user?.value),
        organization_id: orgName,
      };
      updateGroup(values, selectedItem?.id, orgName).then(() => {
        getGroupList(orgName, selectedProduct?.id).then((res) => {
          setGroupList(res?.data);
          const matchedItem = res?.data?.find(
            (item) => item.id === selectedItem?.id
          );
          setSelectedItem(matchedItem);
        });
      });
    },
  });

  useEffect(() => {
    getApplicationList(orgName).then((resp) => {
      const list = resp?.data || [];
      setProductList(list);
      setTriggerGrouplist(true);

      if (list.length > 0) {
        const firstOption = {
          id: list[0]?.id,
          label: list[0]?.name,
        };
        setSelectedProduct(firstOption);
        setGroupList([]);
        setSelectedItem(null);
      } else {
        setSelectedProduct(null);
        setGroupList([]);
        setSelectedItem(null);
      }
    });
  }, [orgName, setTriggerGrouplist]);

  useEffect(() => {
    if (triggerGrouplist && selectedProduct) {
      getGroupList(orgName, selectedProduct.id).then((res) => {
        setGroupList(res?.data);
        if (res?.data?.length > 0) {
          setSelectedItem(res?.data[0]);
        }
      });
      setTriggerGrouplist(false);
    }
  }, [triggerGrouplist, productList, selectedProduct, setGroupList, orgName]);

  const handleItemClick = (item) => {
    setSelectedItem(item);
    formikForm.setFieldValue('name', item?.name);
    formikForm.setFieldValue(
      'users',
      item?.users?.map((data) => {
        return { label: data?.username, value: data?.id };
      })
    );
    formikForm.setFieldValue('description', item?.description);
  };

  useEffect(() => {
    if (groupList?.length > 0) {
      if (selectedItem) {
        handleItemClick(selectedItem);
      } else {
        handleItemClick(filteredGroupList[0]);
      }
    } else {
      setSelectedItem(null);
      formikForm.resetForm();
    }
  }, [groupList, orgName, selectedItem]);

  useEffect(() => {
    fetchUserDetails('', orgName).then((res) => {
      setUserList(res?.data);
    });
  }, [setUserList, orgName]);

  /**
   * @function handleReaderInput
   * @description handles the input field for the product selection in the form.
   * @param {object} params - The parameters for the Autocomplete component.
   * @returns {JSX} - renders The TextField component for Autocomplete
   * @example handleReaderInput(params)
   */
  const handleReaderInput = useCallback(
    (params) => {
      return (
        <TextField
          required
          {...params}
          name="product"
          label="Select Application"
          fullWidth
          placeholder="Select product"
          InputLabelProps={{
            shrink: true,
          }}
          onBlur={formikForm.handleBlur}
        />
      );
    },
    [formikForm]
  );

  const handleDelete = async (id) => {
    try {
      await groupDelete(id, orgName);
      setTriggerGrouplist(true);
    } catch (error) {
      return error;
    }
  };

  const handleDeleteGroup = useCallback(() => {
    setSearchText('');
    handleDelete(selectedItem?.id).then(() => {
      getGroupList(orgName, selectedProduct?.id).then((res) => {
        setGroupList(res?.data);
        setSelectedItem(null);
      });
    });
    setShowDeleteModal(false);
  }, [handleDelete, setShowDeleteModal, selectedItem]);

  useEffect(() => {
    if (selectedItem && tabIndex === 1) {
      const groupId = selectedItem?.id;
      const orgId = orgName;
      getMemberList(groupId, orgId)
        .then((res) => {
          setUserData(res?.data);
        })
        .catch((error) => {
          return error;
        });
    }
  }, [selectedItem, tabIndex]);

  return (
    <Grid container flexDirection="column" spacing={2} padding="20px">
      <Grid container alignItems="center" item xs={12}>
        <Typography
          variant="h3"
          color={theme.palette.text.primary}
          flexGrow="1"
        >
          Groups
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            <Box width="200px" mb={1}>
              <Autocomplete
                options={
                  productList?.map((item) => ({
                    id: item?.id,
                    label: item?.name,
                  })) || []
                }
                value={selectedProduct}
                onChange={(_e, newValue) => {
                  setSelectedProduct(newValue);
                  setTriggerGrouplist(true);
                  setTabIndex(0);
                }}
                popupIcon={<DropdownArrow />}
                noOptionsText="No product available."
                disableClearable
                getOptionLabel={(item) => item?.label || ''}
                isOptionEqualToValue={(option, val) =>
                  option?.label === val?.label
                }
                renderInput={handleReaderInput}
              />
            </Box>

            <Typography
              variant="body4"
              tabIndex={0}
              role="note"
              aria-label="Important note: Please select an application from the dropdown to continue."
              sx={{
                fontStyle: 'normal',
                border: 'none',
                '&:focus': {
                  border: '2px solid',
                  borderColor: 'primary.main',
                },
              }}
            >
              <strong>Note:</strong>&nbsp;
              <i>Please select an application from the dropdown to continue.</i>
            </Typography>
          </Box>
          <Button
            title={
              productList?.length === 0
                ? 'Please add one applicaton before adding group.'
                : 'Create Group'
            }
            variant="contained"
            onClick={handleOpen}
            disabled={productList?.length === 0}
          >
            + Create Group
          </Button>
        </Box>
      </Grid>
      {groupList?.length !== 0 ? (
        <Grid item>
          <Grid container direction="row" spacing={2}>
            <Grid item xs={12} md={3.5}>
              <Grid container direction="column" spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    placeholder="Search"
                    sx={{
                      marginTop: 0,
                    }}
                    InputProps={{
                      sx: {
                        paddingLeft: 1,
                        '& .MuiFilledInput-input': {
                          paddingTop: '10px',
                        },
                      },
                      startAdornment: (
                        <IconButton>
                          <SearchIconNew />
                        </IconButton>
                      ),
                    }}
                    value={searchText}
                    onChange={handleSearchChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  {groupList?.length !== 0 && (
                    <Grid
                      item
                      border={`1px solid ${theme.palette.divider}`}
                      borderRadius="4px"
                      display="flex"
                      flexDirection="column"
                      minHeight="389px"
                      height="calc(100vh - 332px)"
                      backgroundColor={theme.palette.other.groupBg}
                    >
                      {/* Header (fixed) */}
                      <Grid
                        alignItems="center"
                        display="flex"
                        justifyContent="space-between"
                        paddingBottom="12px"
                        borderBottom={`1px solid ${theme.palette.divider}`}
                        pl={1}
                        pt={1}
                      >
                        <Typography variant="subtitle1">All Groups</Typography>
                      </Grid>

                      {/* Scrollable content */}
                      <Box
                        flexGrow={1}
                        overflow="auto"
                        paddingX="8px"
                        paddingY="12px"
                      >
                        {filteredGroupList?.length ? (
                          filteredGroupList?.map((item) => (
                            <Grid
                              key={item?.id}
                              id={item?.id}
                              minHeight="60px"
                              display="flex"
                              alignItems="center"
                              px="16px"
                              mb="8px"
                              borderRadius="4px"
                              onClick={() => handleItemClick(item)}
                              sx={{
                                cursor: 'pointer',
                                backgroundColor:
                                  selectedItem?.id === item?.id
                                    ? theme.palette.other.sidebarMenuSelected
                                    : 'transparent',
                                '&:hover': {
                                  backgroundColor:
                                    theme.palette.other.lightGrey, // hover color
                                },
                              }}
                            >
                              <Typography variant="body1" width="100%">
                                {item?.name}
                              </Typography>
                              <IconButton
                                title="Delete Group"
                                onClick={(e) => {
                                  setShowDeleteModal(true);
                                  setSelectedItem(item);
                                  e.stopPropagation(); // Prevents triggering the item click
                                }}
                              >
                                <DeleteIcon
                                  style={{ color: theme.palette.other.icon }}
                                />
                              </IconButton>
                            </Grid>
                          ))
                        ) : (
                          <Box
                            flexGrow={1}
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            flexDirection="column"
                            height="300px"
                          >
                            <NoDataImage
                              style={{ color: theme.palette.primary.main }}
                            />
                            <Typography
                              variant="h3"
                              textAlign="center"
                              sx={{ pt: '10px' }}
                            >
                              No Data Found
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={8.5}>
              <Grid
                item
                border={`1px solid ${theme.palette.divider}`}
                backgroundColor={theme.palette.other.groupBg}
                borderRadius="4px"
                paddingY="16px"
                paddingX="24px"
                minHeight="450px"
                height="calc(100vh - 270px)"
              >
                <Grid>
                  <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    sx={{ flex: 1 }}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab label="General" id="um_ant_tabs_general" />
                    <Tab label="Permissions" id="um_ant_tabs_login" />
                  </Tabs>
                </Grid>
                <Grid>
                  {tabIndex === 0 ? (
                    <Grid
                      container
                      direction="column"
                      justifyContent="space-between"
                      sx={{ height: '100%' }}
                      py="24px"
                    >
                      {/* Form fields row */}
                      <Grid
                        container
                        direction="column"
                        spacing={2}
                        sx={{ width: '60%' }}
                      >
                        <Grid item>
                          <TextField
                            label="Name"
                            required
                            placeholder="Enter Name"
                            name="name"
                            value={formikForm.values.name}
                            onChange={formikForm.handleChange}
                            onBlur={formikForm.handleBlur}
                            error={
                              formikForm.touched.name &&
                              Boolean(formikForm.errors.name)
                            }
                            helperText={
                              formikForm.touched.name && formikForm.errors.name
                            }
                            fullWidth
                          />
                        </Grid>
                        <Grid item>
                          <MultiSelect
                            label="Users"
                            multiple
                            placeholder="Select users"
                            name="users"
                            value={formikForm?.values?.users}
                            onChange={(e) =>
                              formikForm?.setFieldValue('users', e)
                            }
                            noOptionsText="No users available."
                            limitTags={3}
                            aria-label="users"
                            onBlur={formikForm?.handleBlur}
                            helperText={
                              formikForm?.errors?.users &&
                              formikForm?.touched?.users
                                ? formikForm?.errors?.users
                                : null
                            }
                            error={
                              Boolean(formikForm?.errors?.users) &&
                              formikForm?.touched?.users
                            }
                            options={
                              userList?.length > 0
                                ? userList?.map((item) => ({
                                    label: item?.username,
                                    value: item?.id,
                                  }))
                                : []
                            }
                            getOptionLabel={(option) => option?.label || ''}
                            isOptionEqualToValue={(option, val) =>
                              option?.value === val?.value
                            }
                          />
                        </Grid>
                        <Grid item>
                          <TextField
                            label="Description"
                            placeholder="Enter description"
                            name="description"
                            value={formikForm.values.description}
                            onChange={formikForm.handleChange}
                            onBlur={formikForm.handleBlur}
                            error={
                              formikForm.touched.description &&
                              Boolean(formikForm.errors.description)
                            }
                            helperText={
                              formikForm.touched.description &&
                              formikForm.errors.description
                            }
                            fullWidth
                          />
                        </Grid>
                      </Grid>

                      {/* Buttons */}
                      <Grid
                        item
                        container
                        justifyContent="flex-start"
                        spacing={2}
                        py="16px"
                      >
                        <Grid item>
                          <Button
                            variant="outlined"
                            onClick={formikForm.handleReset}
                            title="Reset"
                          >
                            Reset
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            variant="contained"
                            onClick={formikForm.handleSubmit}
                            title="Submit"
                          >
                            Submit
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  ) : null}
                </Grid>
                <Grid>
                  {tabIndex === 1 ? (
                    <GroupPermission
                      product={selectedProduct}
                      selectedItem={selectedItem}
                      updateGroupPermission={updateGroupPermission}
                      orgName={orgName}
                      userData={userData}
                      getDefaultPermission={getDefaultPermission}
                      client={selectedProduct}
                      getMemberList={getMemberList}
                      setUserData={setUserData}
                    />
                  ) : null}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          overflow="auto"
          minHeight="300px"
          height="calc(100vh - 438px)"
        >
          {searchText ? (
            <NoDataImage style={{ color: theme.palette.primary.main }} />
          ) : (
            <NoDataCreated style={{ color: theme.palette.primary.main }} />
          )}
          <Typography
            variant="subtitle1"
            textAlign="center"
            sx={{ pt: '10px' }}
          >
            {searchText
              ? 'No matching result found.'
              : 'No groups found. Please create a group to proceed.'}
          </Typography>
        </Box>
      )}
      {/* Child 3 with two rows */}

      {productList && open && (
        <GroupDialog
          open={open}
          setOpen={setOpen}
          createGroup={createGroup}
          orgName={orgName}
          setTriggerGrouplist={setTriggerGrouplist}
          setSelectedItem={setSelectedItem}
          client={selectedProduct}
          userList={userList}
        />
      )}

      <DeleteDialog
        open={showDeleteModal}
        onCloseClick={() => setShowDeleteModal(false)}
        onDeleteClick={() => handleDeleteGroup()}
        confirmationMsg={`Are you sure you want to delete "${selectedItem?.name}" group ?`}
        resourceName="Group"
      />
    </Grid>
  );
};

GroupSetting.propTypes = {
  state: PropTypes.oneOfType([PropTypes.object]).isRequired, // Assuming state is an object
  updateGroupPermission: PropTypes.func.isRequired,
};

export default withTransaction('GroupSetting', 'component')(GroupSetting);
